// All prices are exclusive of Xero subscription (R500 / month or R6,000 / annum)
// Each tier (Starter / Core / Elite) ships three packages: Bronze, Silver, Gold
// Monthly retainer; annual = monthly × 12

export const tiers = [
    {
        id: "starter",
        name: "Starter",
        tagline: "For Non-VAT companies",
        description:
            "Designed for newly registered or small companies that are not VAT-registered and require Compiled Annual Financial Statements.",
        descriptionExtra: "Ideal for couple-owned and partner-led businesses.",
        statementType: "Compiled",
    },
    {
        id: "core",
        name: "Core",
        tagline: "For VAT-registered companies",
        description:
            "Built for VAT-registered companies who require Reviewed Financial Statements under ISRE 2400 and structured monthly accounting.",
        descriptionExtra: "Our most popular package. 76% of our clients choose the Core option.",
        statementType: "Reviewed",
    },
    {
        id: "elite",
        name: "Elite",
        tagline: "Audit-ready businesses",
        description:
            "For Non-VAT or VAT-registered companies whose Public Interest Score, MOI or stakeholders require Audited Annual Financial Statements.",
        descriptionExtra:
            "Ideal for estate agents requiring mandatory PPRA audits, public companies, and body corporates governed by statutory audit requirements. It is also the perfect fit for businesses fulfilling bank covenants, or multi-shareholder companies seeking the sacrosanct peace of mind that an audit brings.",
        statementType: "Audited",
    },
];

const baseFeatures = (overrides = {}) => ({
    statementType: overrides.statementType,
    vatRegistered: overrides.vatRegistered ?? true,
    companyTaxReturns: true,
    specialist: overrides.specialist ?? "Accountant",
    workSplit: overrides.workSplit, // share TCA does
    payroll: overrides.payroll ?? "Add-on",
    discussionMeetings: overrides.discussionMeetings ?? "Upon request (billed)",
    managementAccounts: overrides.managementAccounts ?? "Quarterly",
    bookkeeping: true,
    qualifiedBookkeeper: overrides.qualifiedBookkeeper ?? true,
    qualifiedAccountant: overrides.qualifiedAccountant ?? true,
    taxSpecialist: true,
    profitReviews: true,
    aiAutomations: true,
    whatsappSupport: true,
    whatsappSlips: true,
    liveReports: true,
    cloudAccounting: true,
    xeroTraining: true,
    beeAffidavit: true,
    cipcConsultation: true,
    taxReturns: true,
    salesInvoices: overrides.salesInvoices ?? true,
    bankPosting: overrides.bankPosting ?? true,
    supplierBills: overrides.supplierBills ?? true,
    zoom: true,
    vatReturns: overrides.vatReturns ?? true,
});

export const packages = {
    starter: [
        {
            name: "Bronze",
            monthly: 950,
            annual: 11400,
            highlight: false,
            blurb: "Ideal for owner-operated startups still building monthly volume.",
            features: baseFeatures({
                statementType: "Compiled",
                vatRegistered: false,
                workSplit: "TCA does 20%",
                payroll: "2 staff included",
                managementAccounts: "Quarterly",
                specialist: "Bookkeeper",
                qualifiedAccountant: false,
                vatReturns: false,
                salesInvoices: false,
                bankPosting: false,
                supplierBills: false,
            }),
        },
        {
            name: "Silver",
            monthly: 1450,
            annual: 17400,
            highlight: true,
            blurb: "Most-loved Starter package — TCA handles the bulk of accounting.",
            features: baseFeatures({
                statementType: "Compiled",
                vatRegistered: false,
                workSplit: "TCA does 50%",
                payroll: "5 staff included",
                managementAccounts: "Quarterly",
                qualifiedBookkeeper: false,
                vatReturns: false,
                salesInvoices: false,
                supplierBills: false,
            }),
        },
        {
            name: "Gold",
            monthly: 1995,
            annual: 23940,
            highlight: false,
            blurb: "TCA runs your books end-to-end while you focus on growth.",
            features: baseFeatures({
                statementType: "Compiled",
                vatRegistered: false,
                workSplit: "TCA does 80%",
                payroll: "10 staff included",
                discussionMeetings: "Every 2 months",
                managementAccounts: "Every 2 months",
                qualifiedBookkeeper: false,
                vatReturns: false,
            }),
        },
    ],
    core: [
        {
            name: "Bronze",
            monthly: 1950,
            annual: 23400,
            highlight: false,
            blurb: "VAT-registered SMEs sharing the bookkeeping load with TCA.",
            features: baseFeatures({
                statementType: "Reviewed",
                vatRegistered: true,
                workSplit: "TCA does 20%",
                payroll: "5 staff included",
                discussionMeetings: "Upon request (billed)",
                managementAccounts: "Quarterly",
                specialist: "Bookkeeper",
                qualifiedAccountant: false,
                salesInvoices: false,
                bankPosting: false,
                supplierBills: false,
            }),
        },
        {
            name: "Silver",
            monthly: 2950,
            annual: 35400,
            highlight: true,
            blurb: "Recommended Core tier — full reviewed AFS and faster reporting.",
            features: baseFeatures({
                statementType: "Reviewed",
                vatRegistered: true,
                workSplit: "TCA does 50%",
                payroll: "20 staff included",
                discussionMeetings: "Every 2 months",
                managementAccounts: "Every 2 months",
                qualifiedBookkeeper: false,
                salesInvoices: false,
                supplierBills: false,
            }),
        },
        {
            name: "Gold",
            monthly: 4650,
            annual: 55800,
            highlight: false,
            blurb: "TCA-led accounting for established VAT vendors with bigger payrolls.",
            features: baseFeatures({
                statementType: "Reviewed",
                vatRegistered: true,
                workSplit: "TCA does 80%",
                payroll: "40 staff included",
                discussionMeetings: "Monthly",
                managementAccounts: "Every 2 months",
                qualifiedBookkeeper: false,
            }),
        },
    ],
    elite: [
        {
            name: "Bronze",
            monthly: 2650,
            annual: 31800,
            highlight: false,
            blurb: "Audit-ready businesses that handle their own day-to-day capture.",
            features: baseFeatures({
                statementType: "Audited",
                vatRegistered: false,
                workSplit: "TCA does 20%",
                payroll: "15 staff included",
                managementAccounts: "Quarterly",
                specialist: "Bookkeeper",
                qualifiedAccountant: false,
                vatReturns: false,
                salesInvoices: false,
                bankPosting: false,
                supplierBills: false,
            }),
        },
        {
            name: "Silver",
            monthly: 4650,
            annual: 55800,
            highlight: true,
            blurb: "Mid-market favourite — fully reconciled, audit-ready monthly.",
            features: baseFeatures({
                statementType: "Audited",
                vatRegistered: true,
                workSplit: "TCA does 50%",
                payroll: "40 staff included",
                discussionMeetings: "Every 2 months",
                managementAccounts: "Every 2 months",
                qualifiedBookkeeper: false,
                salesInvoices: false,
                supplierBills: false,
            }),
        },
        {
            name: "Gold",
            monthly: 7950,
            annual: 95400,
            highlight: false,
            blurb: "White-glove monthly accounting for high-PIS regulated entities.",
            features: baseFeatures({
                statementType: "Audited",
                vatRegistered: true,
                workSplit: "TCA does 80%",
                payroll: "80 staff included",
                discussionMeetings: "Monthly",
                managementAccounts: "Monthly",
                qualifiedBookkeeper: false,
            }),
        },
    ],
};

export const featureRows = [
    { type: "header", label: "Package Essentials" },
    { key: "statementType", label: "Annual Financial Statements" },
    { key: "companyTaxReturns", label: "Company Tax Returns" },
    { key: "specialist", label: "Specialist" },
    { key: "vatRegistered", label: "VAT-Registered Company" },
    { key: "workSplit", label: "Accounting work handled by TCA" },
    { key: "payroll", label: "Payroll + SARS submissions" },
    { key: "discussionMeetings", label: "Advisory Meetings" },
    { key: "managementAccounts", label: "Management Accounts" },
    { type: "header", label: "The TCA Difference" },
    { key: "qualifiedBookkeeper", label: "Designated Qualified Bookkeeper" },
    { key: "qualifiedAccountant", label: "Designated SAIPA/SAICA Accountant" },
    { key: "taxSpecialist", label: "Designated Tax & Financial Specialist" },
    { key: "profitReviews", label: "Business Profit Reviews & Tax Savings" },
    { key: "aiAutomations", label: "A.I. Process Automations (Zapier + ChatGPT)" },
    { key: "whatsappSupport", label: "Dedicated WhatsApp Support Group" },
    { type: "header", label: "Software" },
    { key: "whatsappSlips", label: "WhatsApp Photos of Slips / Expenses" },
    { key: "liveReports", label: "Live Accounting / Business Reports" },
    { key: "cloudAccounting", label: "World-Leading Cloud Accounting (Xero)" },
    { key: "xeroTraining", label: "Tailored Xero Training for Staff" },
    { type: "header", label: "Compliance" },
    { key: "beeAffidavit", label: "BEE Affidavit Certificates" },
    { key: "cipcConsultation", label: "CIPC Consultation & Guidance" },
    { key: "taxReturns", label: "Tax Returns + Provisional Tax Returns" },
    { key: "vatReturns", label: "VAT Return Review + SARS Submission" },
    { type: "header", label: "Bookkeeping" },
    { key: "salesInvoices", label: "Preparing Sales Invoices" },
    { key: "bankPosting", label: "Posting Bank Transactions" },
    { key: "supplierBills", label: "Posting Expense / Supplier Bills" },
];

export const xeroAddOn = {
    name: "Xero Cloud Accounting Subscription",
    monthly: 500,
    annual: 6000,
    note: "Required for all packages. Includes the Xero Starter plan licensed via TCA Gold Partner pricing.",
};
